import { useAuth } from '@/hooks/useAuth';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import { useTranslation } from '@/hooks/useTranslation';
import { getRedirectTarget, isLogoutNavigationState } from '@/lib/redirectTo';
import { Navigate } from 'react-router-dom';

interface PublicRouteProps {
  children: React.ReactNode;
}

/**
 * LoadingSpinner component for reuse across the application
 */
const LoadingSpinner = () => {
  const { t } = useTranslation();

  return (
    <div className='flex items-center justify-center min-h-screen'>
      <div className='text-center'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto' />
        <p className='mt-2 text-muted-foreground'>{t('loading')}</p>
      </div>
    </div>
  );
};

/**
 * PublicRoute component that redirects authenticated users to the dashboard
 * Follows KISS principle with single responsibility
 */
export const PublicRoute = ({ children }: PublicRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isLoginPage, searchParams, state } = useCurrentRoute();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // While a logout is in progress the session is still active for a moment,
  // so the user must stay on the login page instead of being sent back
  const isLoggingOut = isLogoutNavigationState(state);

  return isAuthenticated && isLoginPage && !isLoggingOut ? (
    <Navigate to={getRedirectTarget(searchParams)} replace />
  ) : (
    <>{children}</>
  );
};

export { LoadingSpinner };
